import { getAllExercises, getExerciseById, type IExercise } from './exercises';
import { getTrainingExperienceSettings, isHomeExercise } from './training-environment';
import type { ITrainingPlanDay } from './training-plan-generator';
import type { ExerciseConfiguration } from '@/lib/memberTrainingDay';
import { trainingClock, type PersonalSchedule } from '@/lib/personalSchedule';

/** Use the existing demonstrations; optional equipment is never assumed to be owned. */
export function hasAvailableEquipment(exercise: IExercise, equipment?: string[]): boolean {
  if (!equipment) return true; // Old plans retain their original environment rules.
  const owned = new Set(['bodyweight', ...equipment]);
  if (!owned.has(exercise.equipment)) return false;
  const guide = [exercise.name, ...exercise.tips, ...(exercise.detailGuide || [])].join(' ');
  if (/哑铃/.test(guide) && !owned.has('dumbbell')) return false;
  if (/弹力带/.test(guide) && !owned.has('resistance-band')) return false;
  if (/壶铃/.test(guide) && !owned.has('kettlebell')) return false;
  if (/泡沫轴/.test(guide) && !owned.has('foam-roller')) return false;
  return true;
}
export function equipmentTrainingIds(ids: string[], equipment?: string[], home = false, alternatives = false): string[] {
  if (!equipment) return ids;
  const accepts = (ex: IExercise) => hasAvailableEquipment(ex, equipment) && (!home || isHomeExercise(ex));
  const result = ids.filter(id => { const ex = getExerciseById(id); return !!ex && accepts(ex); });
  if (result.length || !alternatives) return result;
  const parts = new Set(ids.map(id => getExerciseById(id)?.bodyPart));
  return getAllExercises().filter(ex => !ex.isWarmup && !ex.isStretch && parts.has(ex.bodyPart) && accepts(ex)).slice(0, 4).map(ex => ex.id);
}
function setSeconds(ex: IExercise, reps: string): number {
  const numbers = reps.match(/\d+(?:\.\d+)?/g)?.map(Number) || [10];
  const count = Math.max(...numbers);
  return /分钟/.test(reps) ? count * 60 : /秒/.test(reps) ? count : count * 3;
}
export function estimateTrainingMinutes(day: ITrainingPlanDay, settings: Record<string, ExerciseConfiguration>): number {
  const ids = [...(day.warmupIds || []), ...(day.exerciseIds || []), ...(day.stretchIds || [])];
  const seconds = ids.reduce((sum, id) => {
    const ex = getExerciseById(id); if (!ex) return sum;
    const c = settings[id] || { sets: ex.sets, reps: ex.reps, restSeconds: ex.restSeconds };
    return sum + c.sets * setSeconds(ex, c.reps) + Math.max(0, c.sets - 1) * c.restSeconds + 30;
  }, 0);
  return Math.ceil(seconds / 60);
}
/** Reduce sets before removing trailing actions. Keep course order and each teacher identity. */
export function fitTrainingTime(day: ITrainingPlanDay, minutes: number, months: number) {
  const experience = getTrainingExperienceSettings(months);
  const result = { ...day, exerciseIds: [...(day.exerciseIds || [])], warmupIds: [...(day.warmupIds || [])], stretchIds: [...(day.stretchIds || [])] };
  const settings: Record<string, ExerciseConfiguration> = {};
  for (const id of [...result.warmupIds, ...result.exerciseIds, ...result.stretchIds]) {
    const ex = getExerciseById(id); if (!ex) continue;
    const main = result.exerciseIds.includes(id);
    settings[id] = { sets: main ? experience.sets : ex.isStretch || ex.bodyPart === 'stretch' ? ex.sets : 1,
      reps: main && !/秒|分钟/.test(ex.reps) ? experience.reps : ex.reps,
      restSeconds: main ? experience.restSec : ex.restSeconds };
  }
  const estimate = () => estimateTrainingMinutes(result, settings);
  for (const id of [...result.exerciseIds].reverse()) {
    while (estimate() > minutes && settings[id]?.sets > 1) settings[id].sets--;
  }
  while (estimate() > minutes && result.exerciseIds.length > 1) result.exerciseIds.pop();
  while (estimate() > minutes && result.stretchIds.length > 1) result.stretchIds.pop();
  while (estimate() > minutes && result.warmupIds.length > 1) result.warmupIds.pop();
  const durationMin = estimate();
  if (!result.exerciseIds.length || durationMin > minutes) return { ...result, type: 'active-rest' as const,
    label: '恢复安排', subtitle: '当前器械、不适或时间条件不足以生成完整力量课', durationMin: 0,
    exerciseIds: [], warmupIds: [], stretchIds: [], exerciseSettings: {}, note: '可调整问卷条件后重新生成' };
  return { ...result, durationMin, exerciseSettings: settings,
    note: [result.note, `按${minutes}分钟上限编排，含热身、组间休息与拉伸；实际用时随动作速度变化`].filter(Boolean).join(' · ') };
}
export function personalizedTrainingDay(day: ITrainingPlanDay, preferences: PersonalSchedule, minutes: number, months: number) {
  const fitted = day.type === 'strength' ? fitTrainingTime(day, minutes, months)
    : { ...day, durationMin: day.type === 'rest' ? 0 : Math.min(day.durationMin, minutes) };
  return { ...fitted, startTime: fitted.type === 'rest' ? undefined : trainingClock(preferences, day.dayOfWeek) };
}
